// AGENT-SPEC (docs/design/2026-10-31-consolidate-then-demo.md §2, §2.1, A3): the one home of each
// agent's setup, and the signed bundle a container pulls.
//
//   GET  {co}/digital-employees/{id}/agent-spec                  the current version and history
//   PUT  {co}/digital-employees/{id}/agent-spec                  a change → a new signed version
//   GET  {co}/digital-employees/{id}/agent-spec/versions/{version}  one version in full
//   GET  /v1/agent-spec/{container}       the container's signed bundle (its own token only): v2
//                                         (./agent-spec-v2.ts); `?format=v1` this file's, deprecated
//   POST /v1/agent-spec/applied           v1, deprecated: the container reports the version applied
//
// DEPRECATED: `companyos.agent-spec/v1` and its bundle stay readable for one release, then go.
// `companyos.agent-spec/v2` (./agent-spec-v2.ts) is the one spec format.
//   GET  /v1/agent-spec-keys              the public keys bundles are signed with
//   POST /v1/aigentpanel/container-identity   the panel registers a container's token (HMAC)
//   GET  /v1/operator/agent-containers    every container: desired vs applied (drift)
//
// A spec holds key REFERENCES (an env name and where the value lives), never a value: every
// string in a spec is scanned for secret-shaped text and refused (`findSecretLiterals`). A part
// left null is not managed by CompanyOS yet: the applier leaves it as the container has it (how
// today's panel-owned settings move in one part at a time, A12).
import { z } from "zod";
import {
  AgentContainerHealth,
  AgentContainerStatus,
  AgentSpecApplyStatus,
  AgentSpecPart,
  AgentSpecSource,
} from "../enums";
import { boundedRecord, Timestamp, Uuid } from "../primitives";
import { CompanyItemParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";

export const AGENT_SPEC_FORMAT = "companyos.agent-spec/v1";
export const AGENT_SPEC_BUNDLE_FORMAT = "companyos.agent-spec-bundle/v1";
/** The container token: `cosk_ctr_` + 43 base64url characters (256 bits), issued by the panel. */
export const CONTAINER_TOKEN_PREFIX = "cosk_ctr_";
export const CONTAINER_TOKEN = /^cosk_ctr_[A-Za-z0-9_-]{43}$/;
/** The only paths a container token is presented on (anywhere else it is 401). */
export const isContainerTokenPath = (path: string): boolean =>
  path === "/v1/agent-spec-keys" || path.startsWith("/v1/agent-spec/");

// --- secret scan --------------------------------------------------------------------------------

/** Text that is a credential by its shape (prefixes of keys we and the vendors issue). */
const SECRET_SHAPES: readonly RegExp[] = [
  /\bsk-[A-Za-z0-9_-]{16,}/,
  /\bsk_(?:live|test)_[A-Za-z0-9]{16,}/,
  /\bcos(?:k|r)_[a-z]{0,4}_?[A-Za-z0-9_-]{20,}/,
  /\bgh[pousr]_[A-Za-z0-9]{20,}/,
  /\bxox[abprs]-[A-Za-z0-9-]{10,}/,
  /\bAKIA[0-9A-Z]{16}\b/,
  /\bAIza[0-9A-Za-z_-]{30,}/,
  /-----BEGIN [A-Z ]*PRIVATE KEY-----/,
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\./,
  /\b(?:Bearer|Basic|Token)\s+(?!\$\{)[A-Za-z0-9._~+/=-]{12,}/i,
];
/** Whether `text` holds a credential by its shape (for file content: prose and code). */
export const hasSecretShape = (text: string): boolean => SECRET_SHAPES.some((r) => r.test(text));
/** A long unbroken token (an API key without a known prefix) outside prose. */
const OPAQUE_TOKEN = /[A-Za-z0-9_+=-]{40,}/;
/** Prose fields: SOUL, AGENTS, "How I work" (hashes and long words may appear there). */
const PROSE = new Set(["soul", "agents", "how_i_work.text"]);
/** Fields that hold a SHA-256 by contract. */
const HASH_FIELD = /^skills\.\d+\.sha256$/;

/**
 * The paths of every string in `value` that looks like a secret. A spec carries references only;
 * the API refuses a document with any hit (so does the schema's refinement below).
 */
export function findSecretLiterals(value: unknown, path = ""): string[] {
  if (typeof value === "string") {
    const bare = path.replace(/^\.+/, "");
    if (HASH_FIELD.test(bare)) return [];
    if (SECRET_SHAPES.some((r) => r.test(value))) return [bare || "body"];
    if (!PROSE.has(bare) && OPAQUE_TOKEN.test(value.replace(/\$\{[A-Z][A-Z0-9_]*\}/g, "")))
      return [bare || "body"];
    return [];
  }
  if (Array.isArray(value)) return value.flatMap((v, i) => findSecretLiterals(v, `${path}.${i}`));
  if (value && typeof value === "object")
    return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) => [
      ...(SECRET_SHAPES.some((r) => r.test(k)) ? [`${path}.${k}`.replace(/^\.+/, "")] : []),
      ...findSecretLiterals(v, `${path}.${k}`),
    ]);
  return [];
}

/** One line that is stored and shown (a version's note, a retire reason): never a secret. */
export const SafeLine = z
  .string()
  .trim()
  .min(1)
  .max(300)
  .refine((s) => findSecretLiterals({ line: s }).length === 0, {
    message: "looks like a secret value: write what changed, not a key",
  });

// --- parts --------------------------------------------------------------------------------------

/** A gateway model alias (letters, digits and `-_.:/@`). */
const ModelName = z
  .string()
  .max(200)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,199}$/, "a model name: letters, digits and -_.:/@");
const Name = z
  .string()
  .max(64)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/, "letters, digits and -_.");
/** An environment variable name. */
const EnvName = z
  .string()
  .max(64)
  .regex(/^[A-Z][A-Z0-9_]{1,63}$/, "an environment variable name");
const ENV_REF = /\$\{([A-Z][A-Z0-9_]{1,63})\}/g;
/** Prose (SOUL.md, AGENTS.md, "How I work"): at most 200 000 characters. */
const SpecText = z.string().min(1).max(200_000);
const ToolName = z
  .string()
  .max(128)
  .regex(/^[A-Za-z0-9_][A-Za-z0-9_.:-]{0,127}$/, "a tool name");
const ToolsetName = z
  .string()
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, "a toolset name");
const Sha256 = z
  .string()
  .length(64)
  .regex(/^[0-9a-f]{64}$/, "64 lowercase hex characters");
const ToolList = z.array(ToolName).max(500);

/** `agent.reasoning_effort` as Hermes 0.22 takes it. */
export const SpecEffort = z.enum(["low", "medium", "high"]);

export const SpecModel = z
  .strictObject({
    /** `model.default`: the gateway alias. */
    default: ModelName,
    /** `model.provider` (the gateway's provider entry); null: Hermes' own default. */
    provider: Name.nullable(),
    /** `agent.reasoning_effort`; null: leave the profile's. */
    effort: SpecEffort.nullable(),
    /** `agent.image_input_mode: native` when true (a vision model). */
    vision: z.boolean(),
    /** `auxiliary.<task>.model` (vision, web_extract, compression …), on the main provider. */
    auxiliary: boundedRecord(ModelName, 16, Name).nullable(),
    /** The alias needs prompt caching declared (Hermes keys it on "claude" in the name). */
    prompt_caching: z.boolean(),
  })
  .meta({ id: "AgentSpecModel" });

export const SpecSkillPin = z
  .strictObject({
    /** The skill's frontmatter name. */
    name: Name,
    /** Where it comes from: the platform pack, the company's own, or the profile's local copy. */
    source: z.enum(["platform", "company", "profile"]),
    /** The pinned version (a pack version or a content tag). */
    version: z
      .string()
      .max(64)
      .regex(/^[A-Za-z0-9][A-Za-z0-9._+-]{0,63}$/),
    /** SHA-256 of the skill's files as pinned; null: not pinned by content. */
    sha256: Sha256.nullable(),
    /** Read-only to the agent (the pinned skill's files are not the agent's to edit). */
    pinned: z.boolean(),
  })
  .meta({ id: "AgentSpecSkillPin" });

export const SpecToolsets = z
  .strictObject({
    /** `agent.disabled_toolsets`. */
    disabled: z.array(ToolsetName).max(64),
    /** `platform_toolsets.<platform>`; null: Hermes' composite default per platform. */
    platforms: z
      .partialRecord(
        z.enum(["api_server", "cli", "telegram", "cron"]),
        z.array(ToolsetName).max(64),
      )
      .meta({ maxProperties: 4 })
      .nullable(),
  })
  .meta({ id: "AgentSpecToolsets" });

/** A header value: `${ENV}` (optionally after a scheme word) or a short plain literal. */
const HEADER_REF = /^(?:(?:Bearer|Basic|Token) )?\$\{[A-Z][A-Z0-9_]{1,63}\}$/;
const HEADER_LITERAL = /^[A-Za-z0-9._:/ -]{0,80}$/;
const CREDENTIAL_HEADER = /auth|key|token|secret|cookie|session|password/i;

export const SpecMcpServer = z
  .strictObject({
    name: Name,
    /** The server's URL; `${ENV}` placeholders allowed (the applier fills them from the env). */
    url: z
      .string()
      .max(500)
      .regex(/^(?:https?:\/\/|\$\{[A-Z][A-Z0-9_]{1,63}\})[^\s@]*$/, "an http(s) URL or ${ENV}…"),
    /** Request headers; a credential header must be a `${ENV}` reference. */
    headers: boundedRecord(
      z.string().max(120),
      16,
      z
        .string()
        .max(64)
        .regex(/^[A-Za-z0-9-]{1,64}$/),
    ),
    tools: z.strictObject({
      /** `tools.include`: only these register; null: every tool the server offers. */
      include: ToolList.nullable(),
      exclude: ToolList.nullable(),
      resources: z.boolean(),
      prompts: z.boolean(),
    }),
  })
  .superRefine((s, ctx) => {
    for (const [k, v] of Object.entries(s.headers)) {
      const ok = HEADER_REF.test(v) || (!CREDENTIAL_HEADER.test(k) && HEADER_LITERAL.test(v));
      if (!ok)
        ctx.addIssue({
          code: "custom",
          path: ["headers", k],
          message: "a credential header is a ${ENV} reference, never a value",
        });
    }
  })
  .meta({ id: "AgentSpecMcpServer" });

/**
 * Where a key's value lives. `keyring`: sealed in CompanyOS (`ref` is the sealing context);
 * `litellm`: the per-run gateway key CompanyOS issues; `recall`: the DE's Recall agent key;
 * `container`: still in the container's own `.env` (today's state, until A12 moves it).
 */
export const SpecKeyRef = z
  .strictObject({
    env: EnvName,
    source: z.enum(["keyring", "litellm", "recall", "container"]),
    ref: z
      .string()
      .max(200)
      .regex(/^[A-Za-z0-9:._/-]{1,200}$/)
      .nullable(),
  })
  .refine((k) => (k.source === "keyring") === (k.ref !== null), {
    message: "a keyring reference names its sealing context; no other source has one",
    path: ["ref"],
  })
  .meta({ id: "AgentSpecKeyRef" });

export const SpecRecall = z
  .strictObject({
    /** `memory.provider: recall` and the `recall` MCP server. */
    enabled: z.boolean(),
    /** The Recall tools the profile registers (`mcp_servers.recall.tools.include`). */
    tools: ToolList,
    /** The env var that carries the DE's Recall agent key (declared under `keys`). */
    key_env: EnvName.nullable(),
  })
  .meta({ id: "AgentSpecRecall" });

/**
 * `config.yaml` paths the spec's own parts own (and `providers`, where a gateway's address
 * lives: never redirected from `config`); `config` may not set them.
 */
const RESERVED_CONFIG =
  /^(?:model|mcp_servers|platform_toolsets|providers|approvals|memory\.provider|agent\.(?:disabled_toolsets|reasoning_effort|image_input_mode)|auxiliary)(?:\.|$)/;
const ConfigPath = z
  .string()
  .max(200)
  .regex(/^[a-z_][a-z0-9_]*(?:\.[a-z0-9_]+){0,5}$/, "a dotted config.yaml path")
  .refine((p) => !RESERVED_CONFIG.test(p), "set by the spec's own part, not by `config`")
  .refine(
    (p) => !/(?:^|[._])(?:api_?key|key|token|secret|password)(?:$|[._])/.test(p),
    "no credential in `config`",
  );
const ConfigValue = z.union([z.string().max(500), z.number().finite(), z.boolean()]);

const parts = {
  /** SOUL.md. */
  soul: SpecText.nullable(),
  /** AGENTS.md. */
  agents: SpecText.nullable(),
  /** The DE's "How I work" (its version in CompanyOS), written into the profile's instructions. */
  how_i_work: z.strictObject({ text: SpecText, version: z.int().min(1) }).nullable(),
  model: SpecModel.nullable(),
  skills: z.array(SpecSkillPin).max(300).nullable(),
  toolsets: SpecToolsets.nullable(),
  /** The approvals deny list: commands and tool patterns the profile may never run. */
  approvals: z.strictObject({ deny: z.array(z.string().min(1).max(200)).max(200) }).nullable(),
  mcp_servers: z.array(SpecMcpServer).max(16).nullable(),
  recall: SpecRecall.nullable(),
  keys: z.array(SpecKeyRef).max(32).nullable(),
  /** Other `config.yaml` values by dotted path (history presets, tool output caps …). */
  config: boundedRecord(ConfigValue, 200, ConfigPath).nullable(),
};

type Parts = { [K in keyof typeof parts]: z.infer<(typeof parts)[K]> };
export type AgentSpecPartName = z.infer<typeof AgentSpecPart>;
// The document's parts are exactly the `agentSpecPart` enum (the event's `changed` list).
type SameKeys<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const PARTS_MATCH: SameKeys<keyof typeof parts, AgentSpecPartName> = true;
void PARTS_MATCH;
export const AGENT_SPEC_PARTS: readonly AgentSpecPartName[] = AgentSpecPart.options;

function refineDoc(d: { [K in keyof Parts]?: Parts[K] | undefined }, ctx: z.RefinementCtx) {
  const dup = (label: string, keys: string[]) => {
    const seen = new Set<string>();
    for (const k of keys) {
      if (seen.has(k))
        ctx.addIssue({ code: "custom", path: [label], message: `${label}: "${k}" twice` });
      seen.add(k);
    }
  };
  if (d.skills)
    dup(
      "skills",
      d.skills.map((s) => `${s.source}:${s.name}`),
    );
  if (d.mcp_servers)
    dup(
      "mcp_servers",
      d.mcp_servers.map((s) => s.name),
    );
  if (d.keys)
    dup(
      "keys",
      d.keys.map((k) => k.env),
    );
  // Every ${ENV} a server references, and Recall's key, is declared under `keys` (when the spec
  // manages keys): the applier never fills a variable nobody declared.
  if (d.keys) {
    const declared = new Set(d.keys.map((k) => k.env));
    const refs = new Set<string>();
    for (const s of d.mcp_servers ?? [])
      for (const v of [s.url, ...Object.values(s.headers)])
        for (const m of v.matchAll(ENV_REF)) refs.add(m[1] as string);
    if (d.recall?.key_env) refs.add(d.recall.key_env);
    for (const r of refs)
      if (!declared.has(r) && !PLATFORM_ENV.has(r))
        ctx.addIssue({ code: "custom", path: ["keys"], message: `\${${r}} is not declared` });
  }
  const hits = findSecretLiterals(d);
  for (const p of hits.slice(0, 10))
    ctx.addIssue({
      code: "custom",
      path: p.split(".").filter(Boolean),
      message: "looks like a secret value: a spec holds key references only",
    });
}

/** Non-secret variables every container has (the platform's service addresses, the run id). */
export const PLATFORM_ENV: ReadonlySet<string> = new Set([
  "RECALL_URL",
  "COS_MCP_URL",
  "COMPANYOS_URL",
  "COS_GATEWAY_URL",
  "COS_RUN_ID",
]);

/** One version's document. */
export const AgentSpecDoc = z
  .strictObject({ format: z.literal(AGENT_SPEC_FORMAT), ...parts })
  .superRefine(refineDoc)
  .meta({ id: "AgentSpecDoc" });
export type AgentSpecDoc = z.infer<typeof AgentSpecDoc>;

/** Parts to change: each one named replaces that part whole (null: CompanyOS stops managing it). */
export const AgentSpecChanges = z
  .strictObject(
    Object.fromEntries(Object.entries(parts).map(([k, v]) => [k, v.optional()])) as {
      [K in keyof typeof parts]: z.ZodOptional<(typeof parts)[K]>;
    },
  )
  .superRefine(refineDoc)
  .meta({ id: "AgentSpecChanges" });
export type AgentSpecChanges = z.infer<typeof AgentSpecChanges>;

/** The empty document (nothing managed): the base of a DE's first version. */
export const EMPTY_AGENT_SPEC: AgentSpecDoc = {
  format: AGENT_SPEC_FORMAT,
  soul: null,
  agents: null,
  how_i_work: null,
  model: null,
  skills: null,
  toolsets: null,
  approvals: null,
  mcp_servers: null,
  recall: null,
  keys: null,
  config: null,
};

// --- company side -------------------------------------------------------------------------------

export const AgentSpecVersionSummary = z
  .object({
    version: z.int().min(1),
    spec_sha256: z.string(),
    signing_key_id: z.string(),
    source: AgentSpecSource,
    note: z.string().nullable(),
    created_by_user_id: Uuid.nullable(),
    created_by_membership_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "AgentSpecVersionSummary" });

export const AgentSpecVersion = AgentSpecVersionSummary.extend({
  spec: AgentSpecDoc,
  /** Ed25519 (base64url) over the version's signing payload. */
  signature: z.string(),
}).meta({ id: "AgentSpecVersion" });

/** A container that runs this DE's profile: whether it runs the current version. */
export const AgentSpecContainerSync = z
  .object({
    container_id: Uuid,
    satellite_id: Uuid,
    instance_id: z.string(),
    profile_name: z.string(),
    desired_version: z.int(),
    applied_version: z.int().nullable(),
    apply_status: AgentSpecApplyStatus.nullable(),
    /** The applied bundle carries this DE's current spec version. */
    in_sync: z.boolean(),
  })
  .meta({ id: "AgentSpecContainerSync" });

export const AgentSpecView = z
  .object({
    de_membership_id: Uuid,
    current: AgentSpecVersion.nullable(),
    /** The newest 50, newest first. */
    versions: z.array(AgentSpecVersionSummary),
    containers: z.array(AgentSpecContainerSync),
  })
  .meta({ id: "AgentSpecView" });

export const AgentSpecPut = z
  .strictObject({
    /**
     * The version the change is made on (0: the DE has none yet); another current version is a
     * 409. The editor always sends it; omitted, the change applies to whatever is current.
     */
    base_version: z.int().min(0).optional(),
    changes: AgentSpecChanges,
    note: SafeLine.optional(),
  })
  .meta({ id: "AgentSpecPut" });

export const AgentSpecPutResult = z
  .object({
    /** False: the changes left the document as it was (no new version). */
    changed: z.boolean(),
    current: AgentSpecVersion,
    /** Tool names CompanyOS's MCP server does not offer, and similar non-blocking notes. */
    warnings: z.array(z.string()),
  })
  .meta({ id: "AgentSpecPutResult" });

export const AgentSpecVersionParams = z.object({
  co: Uuid,
  id: Uuid,
  version: z.coerce.number().int().min(1).max(1_000_000),
});

// --- container side -----------------------------------------------------------------------------

export const AgentSpecBundleProfile = z
  .object({
    profile_name: z.string(),
    de_membership_id: Uuid,
    /** False: CompanyOS holds no spec for this DE yet (the applier leaves the profile alone). */
    managed: z.boolean(),
    spec_version: z.int().nullable(),
    spec_sha256: z.string().nullable(),
    spec: AgentSpecDoc.nullable(),
  })
  .meta({ id: "AgentSpecBundleProfile" });

/** The signed payload (decode `payload`, verify, then parse as this). */
export const AgentSpecBundle = z
  .object({
    format: z.literal(AGENT_SPEC_BUNDLE_FORMAT),
    container_id: Uuid,
    company_id: Uuid,
    satellite_id: Uuid,
    instance_id: z.string(),
    /** The container's bundle version: bumped whenever the content changes. */
    version: z.int().min(1),
    /** SHA-256 of the canonical JSON of `{format, container_id, …, profiles}` (no version, no times). */
    content_sha256: z.string(),
    issued_at: Timestamp,
    profiles: z.array(AgentSpecBundleProfile),
  })
  .meta({ id: "AgentSpecBundle" });
export type AgentSpecBundle = z.infer<typeof AgentSpecBundle>;

export const AgentSpecSignedBundle = z
  .object({
    format: z.literal(AGENT_SPEC_BUNDLE_FORMAT),
    alg: z.literal("Ed25519"),
    key_id: z.string(),
    /** base64url of the exact bytes signed: canonical JSON of an `AgentSpecBundle`. */
    payload: z.string(),
    /** base64url Ed25519 signature over the payload bytes. */
    signature: z.string(),
    /** Copies of the payload's version and content hash (unsigned; use the payload's). */
    version: z.int().min(1),
    content_sha256: z.string(),
  })
  .meta({ id: "AgentSpecSignedBundle" });

export const AgentSpecContainerParams = z.object({ container: Uuid });
/** `format=v1`: the deprecated v1 bundle (removed next release); default v2. */
export const AgentSpecFetchQuery = z.object({ format: z.enum(["v1", "v2"]).optional() });

export const AgentSpecAppliedReport = z
  .strictObject({
    container_id: Uuid,
    /** The bundle version the report is about. */
    version: z.int().min(1),
    /** Its `content_sha256`. */
    content_sha256: Sha256,
    status: AgentSpecApplyStatus,
    health: AgentContainerHealth.optional(),
    /** One short line (an error class, a schema check that failed); never a body or a key. */
    detail: z.string().trim().min(1).max(300).optional(),
  })
  .meta({ id: "AgentSpecAppliedReport" });

export const AgentSpecAppliedAck = z
  .object({
    ok: z.literal(true),
    desired_version: z.int(),
    /** The container runs the version CompanyOS wants. */
    in_sync: z.boolean(),
  })
  .meta({ id: "AgentSpecAppliedAck" });

export const AgentSpecSigningKeys = z
  .object({
    keys: z.array(
      z.object({
        key_id: z.string(),
        alg: z.literal("Ed25519"),
        /** The raw 32-byte public key, base64url. */
        public_key: z.string(),
        /** New bundles are signed with this one. */
        current: z.boolean(),
      }),
    ),
  })
  .meta({ id: "AgentSpecSigningKeys" });

// --- the panel and the operator -----------------------------------------------------------------

/**
 * The panel registers the token it issued a container at provisioning (only its SHA-256: the
 * token never leaves the panel and the container), rotates it (a register with a new hash), or
 * retires the container. Ordered by `occurred_at`: an older call never replaces a newer token.
 */
export const ContainerIdentityRequest = z
  .strictObject({
    action: z.enum(["register", "retire"]),
    node_id: z.string().trim().min(1).max(128),
    company_id: Uuid,
    instance_id: z.string().trim().min(1).max(128),
    token_sha256: Sha256.optional(),
    reason: SafeLine.optional(),
    occurred_at: Timestamp,
  })
  .refine((b) => (b.action === "register") === (b.token_sha256 !== undefined), {
    message: "register carries token_sha256; retire does not",
    path: ["token_sha256"],
  })
  .meta({ id: "ContainerIdentityRequest" });

export const ContainerIdentityAck = z
  .object({
    ok: z.literal(true),
    duplicate: z.boolean(),
    container_id: Uuid,
    token_generation: z.int(),
  })
  .meta({ id: "ContainerIdentityAck" });

/**
 * AUTO-PROVISION: agentd's one exchange of the enrollment token ClawPanel placed in its container
 * (`cosk_enr_…`, single use, 2 h) for its container credential, returned once.
 */
export const AgentContainerBootstrap = z
  .strictObject({
    token: z
      .string()
      .max(52)
      .regex(/^cosk_enr_[A-Za-z0-9_-]{43}$/),
  })
  .meta({ id: "AgentContainerBootstrap" });

export const AgentContainerEnrolled = z
  .object({
    container_token: z.string().regex(CONTAINER_TOKEN),
    container_id: z.string(),
    company_id: Uuid,
    de_membership_id: Uuid,
    profiles: z.array(z.string()),
  })
  .meta({ id: "AgentContainerEnrolled" });

export const AgentContainerView = z
  .object({
    id: Uuid,
    company_id: Uuid,
    satellite_id: Uuid,
    instance_id: z.string(),
    status: AgentContainerStatus,
    token_generation: z.int(),
    desired_version: z.int(),
    applied_version: z.int().nullable(),
    apply_status: AgentSpecApplyStatus.nullable(),
    failed_version: z.int().nullable(),
    apply_detail: z.string().nullable(),
    health: AgentContainerHealth.nullable(),
    reported_at: Timestamp.nullable(),
    last_fetch_at: Timestamp.nullable(),
    /** The container last reported the content CompanyOS wants now. */
    in_sync: z.boolean(),
    /** The spec changed since the container last fetched (a new version waits for it). */
    pending_fetch: z.boolean(),
  })
  .meta({ id: "AgentContainerView" });

export const AgentContainerList = z
  .object({ data: z.array(AgentContainerView) })
  .meta({ id: "AgentContainerList" });

// --- routes -------------------------------------------------------------------------------------

const container = { feature: "core", tag: "agent-spec", access: "container_token" } as const;

export const agentSpecRoutes = {
  "digitalEmployees.agentSpec": defineRoute({
    feature: "core",
    id: "digitalEmployees.agentSpec",
    method: "get",
    path: `${co}/digital-employees/{id}/agent-spec`,
    tag: "digital-employees",
    summary:
      "The DE's agent spec: the current signed version, its history and the containers' sync",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentSpecView },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.putAgentSpec": defineRoute({
    feature: "core",
    id: "digitalEmployees.putAgentSpec",
    method: "put",
    path: `${co}/digital-employees/{id}/agent-spec`,
    tag: "digital-employees",
    summary:
      "Change the DE's agent spec (admin or the seat's supervisor): a new signed version its container pulls",
    access: "manager",
    params: CompanyItemParams,
    body: AgentSpecPut,
    response: { status: 200, schema: AgentSpecPutResult },
    errors: ["conflict"],
    emits: ["agent_spec.versioned"],
  }),
  "digitalEmployees.agentSpecVersion": defineRoute({
    feature: "core",
    id: "digitalEmployees.agentSpecVersion",
    method: "get",
    path: `${co}/digital-employees/{id}/agent-spec/versions/{version}`,
    tag: "digital-employees",
    summary: "One version of the DE's agent spec in full",
    access: "manager",
    params: AgentSpecVersionParams,
    response: { status: 200, schema: AgentSpecVersion },
    errors: [],
    emits: [],
  }),
  // `agentSpec.fetch` (GET /v1/agent-spec/{container}) is in ./agent-profile-spec.ts: it serves
  // the v2 bundle, and this v1 one on `?format=v1` (deprecated) for one release.
  "agentSpec.applied": defineRoute({
    ...container,
    id: "agentSpec.applied",
    method: "post",
    path: "/v1/agent-spec/applied",
    summary:
      "Deprecated (v1, removed next release): the container reports the v1 bundle version it applied, or that applying it failed",
    body: AgentSpecAppliedReport,
    // A control-plane row (the container's applied version); no company event.
    safe: true,
    response: { status: 200, schema: AgentSpecAppliedAck },
    errors: ["conflict"],
    emits: [],
  }),
  "agentSpec.signingKeys": defineRoute({
    ...container,
    id: "agentSpec.signingKeys",
    method: "get",
    path: "/v1/agent-spec-keys",
    summary: "The public keys spec bundles are signed with (a container token reads them)",
    response: { status: 200, schema: AgentSpecSigningKeys },
    errors: [],
    emits: [],
  }),
  "aigentpanel.containerIdentity": defineRoute({
    feature: "core",
    tag: "operator",
    access: "public",
    id: "aigentpanel.containerIdentity",
    method: "post",
    path: "/v1/aigentpanel/container-identity",
    summary:
      "The panel registers (or rotates) the token it issued a container, or retires the container (HMAC; the token's hash only)",
    body: ContainerIdentityRequest,
    // A control-plane row and its audit line; no company event.
    safe: true,
    response: { status: 200, schema: ContainerIdentityAck },
    errors: ["signature_invalid", "not_found", "conflict"],
    emits: [],
  }),
  "agentContainers.bootstrap": defineRoute({
    feature: "core",
    tag: "agent-spec",
    access: "public",
    id: "agentContainers.bootstrap",
    method: "post",
    path: "/v1/agent-containers/bootstrap",
    summary:
      "A container's agent exchanges its one-time enrollment token for its container credential (returned once; any failure is 404)",
    body: AgentContainerBootstrap,
    // A control-plane row and the case's readiness evidence; no company event.
    safe: true,
    response: { status: 200, schema: AgentContainerEnrolled },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.agentContainers.list": defineRoute({
    feature: "core",
    tag: "operator",
    access: "operator",
    id: "operator.agentContainers.list",
    method: "get",
    path: "/v1/operator/agent-containers",
    summary: "Every container: the spec bundle version wanted and the one it runs (drift)",
    response: { status: 200, schema: AgentContainerList },
    errors: [],
    emits: [],
  }),
} as const;
